import React, { useState, useEffect, useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
  Dialog,
  TextField,
  Card,
  ScrollArea,
  Separator,
  Link,
} from "@radix-ui/themes";
import {
  IconPlus,
  IconTrash,
  IconSearch,
  IconReload,
  IconUsers,
} from "@tabler/icons-react";
import { useCreatorSearch } from "@/services/creator";
import fetchJson from "@/lib/fetchJson";

interface TrackedCreatorsSectionProps {
  campaignId: number;
  onUpdate?: () => void;
}

interface TrackedCreator {
  id: string;
  creatorPrice: {
    id: number;
    username: string;
    platform: string;
    followers?: number;
    lastScraped?: string;
    lastAttempted?: string;
    creator: {
      id: number;
      name: string;
      creatorType?: {
        tag: string;
      };
    };
  };
  createdAt: string;
}

const TrackedCreatorsSection: React.FC<TrackedCreatorsSectionProps> = ({
  campaignId,
  onUpdate,
}) => {
  // Helper function to format date for display
  const formatLastScraped = (lastScraped?: string, lastAttempted?: string) => {
    const date = lastScraped;
    if (!date) return "Never scraped";

    const scraped = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - scraped.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return "Scraped <1h ago";
    if (diffHours < 24) return `Scraped ${diffHours}h ago`;
    if (diffDays === 1) return "Scraped yesterday";
    if (diffDays < 7) return `Scraped ${diffDays} days ago`;
    if (diffDays < 30) return `Scraped ${Math.floor(diffDays / 7)} weeks ago`;
    return `Scraped ${Math.floor(diffDays / 30)} months ago`;
  };

  // Helper function to get scrape status badge color
  const getScrapeBadgeColor = (
    lastScraped?: string,
    lastAttempted?: string
  ) => {
    const date = lastScraped || lastAttempted;
    if (!date) return "red";

    const scraped = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - scraped.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 24) return "green";
    if (diffDays <= 3) return "yellow";
    if (diffDays <= 7) return "orange";
    return "red";
  };

  const [trackedCreators, setTrackedCreators] = useState<TrackedCreator[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isScrapeDialogOpen, setIsScrapeDialogOpen] = useState(false);
  const [selectedCreatorPriceId, setSelectedCreatorPriceId] = useState<
    number | null
  >(null);
  const [lookbackDays, setLookbackDays] = useState("7");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCreator, setSelectedCreator] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [scrapingCreators, setScrapingCreators] = useState<Set<number>>(
    new Set()
  );
  const [bulkAddingFromBudgets, setBulkAddingFromBudgets] = useState(false);

  // Helper function to generate social media URL
  const getSocialMediaUrl = (platform: string, username: string) => {
    const cleanUsername = username.startsWith("@")
      ? username.slice(1)
      : username;

    switch (platform.toLowerCase()) {
      case "instagram":
        return `https://instagram.com/${cleanUsername}`;
      case "tiktok":
        return `https://tiktok.com/@${cleanUsername}`;
      case "twitter":
      case "x":
        return `https://x.com/${cleanUsername}`;
      case "youtube":
        return `https://youtube.com/@${cleanUsername}`;
      default:
        return null;
    }
  };

  // Search for creators with debouncing
  const { data: searchResults, error: searchError } = useCreatorSearch(
    searchQuery,
    "",
    isDialogOpen && searchQuery.length >= 2
  );

  // Fetch tracked creators
  const fetchTrackedCreators = async () => {
    try {
      const data = await fetchJson(`campaign-creators/${campaignId}`);
      if (data?.result?.error) {
        return;
      } else {
        setTrackedCreators(data);
      }
    } catch (error) {
      console.error("Error fetching tracked creators:", error);
    }
  };

  // Add creator to tracking
  const addCreatorToTracking = async () => {
    if (!selectedCreator) return;

    setLoading(true);
    try {
      await fetchJson(`campaign-creators/${campaignId}`, {
        method: "POST",
        body: JSON.stringify({
          creatorPriceId: selectedCreator.id,
        }),
      });

      await fetchTrackedCreators();
      setIsDialogOpen(false);
      setSelectedCreator(null);
      setSearchQuery("");
      onUpdate?.();
    } catch (error) {
      console.error("Error adding creator to tracking:", error);
    } finally {
      setLoading(false);
    }
  };

  // Remove creator from tracking
  const removeCreatorFromTracking = async (creatorPriceId: number) => {
    try {
      await fetchJson(`campaign-creators/${campaignId}`, {
        method: "DELETE",
        body: JSON.stringify({
          creatorPriceId,
        }),
      });

      await fetchTrackedCreators();
      onUpdate?.();
    } catch (error) {
      console.error("Error removing creator from tracking:", error);
    }
  };

  // Open scrape dialog
  const openScrapeDialog = (creatorPriceId: number) => {
    setSelectedCreatorPriceId(creatorPriceId);
    setLookbackDays("7");
    setIsScrapeDialogOpen(true);
  };

  // Force scrape creator
  const forceScrapeCreator = async () => {
    if (!selectedCreatorPriceId) return;

    const days = parseInt(lookbackDays);
    if (isNaN(days) || days < 1 || days > 30) {
      alert("Please enter a valid number of lookback days (1-30)");
      return;
    }

    setScrapingCreators((prev) => new Set(prev).add(selectedCreatorPriceId));
    setIsScrapeDialogOpen(false);

    try {
      await fetchJson(
        `creator/price/${selectedCreatorPriceId}/scrape?lookback_days=${days}`,
        {
          method: "POST",
        }
      );

      // Wait a moment then refresh the data to show updated lastAttempted
      setTimeout(async () => {
        await fetchTrackedCreators();
      }, 1000);
    } catch (error) {
      console.error("Error force scraping creator:", error);
    } finally {
      setScrapingCreators((prev) => {
        const next = new Set(prev);
        next.delete(selectedCreatorPriceId);
        return next;
      });
    }
  };

  // Bulk add all creators from campaign budgets
  const addAllFromBudgets = async () => {
    const confirmed = window.confirm(
      "This will track all creators from your campaign budgets REGARDLESS OF SOUND, HASHTAGS, OR MENTIONS. Their posts will be automatically scraped and added to this campaign. Continue?"
    );
    if (!confirmed) return;

    setBulkAddingFromBudgets(true);
    try {
      const result = await fetchJson(`campaign-creators/${campaignId}/bulk`, {
        method: "POST",
      });

      if (result?.added > 0) {
        await fetchTrackedCreators();
        onUpdate?.();
      }
    } catch (error) {
      console.error("Error bulk adding creators from budgets:", error);
    } finally {
      setBulkAddingFromBudgets(false);
    }
  };

  useEffect(() => {
    fetchTrackedCreators();
  }, [campaignId]);

  // Filter out creators that are already being tracked
  const availableCreatorPrices = useMemo(() => {
    if (!searchResults?.creators) return [];

    const trackedPriceIds = trackedCreators.map((t) => t.creatorPrice.id);

    return searchResults.creators.flatMap(
      (creator) =>
        creator.prices
          ?.filter((price) => !trackedPriceIds.includes(price.id))
          .map((price) => ({
            id: price.id,
            creatorName: creator.name,
            username: price.username,
            platform: price.platform,
            followers: price.followers,
            creatorType: creator.creatorType,
            starred: creator.starred,
          })) || []
    );
  }, [searchResults, trackedCreators]);

  const handleCreatorSelect = (creatorPrice: any) => {
    setSelectedCreator(creatorPrice);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSearchQuery("");
    setSelectedCreator(null);
  };
  console.log({ trackedCreators });

  return (
    <div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .spinning {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `,
        }}
      />
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Heading size="3">Tracked Creators</Heading>
          <Flex gap="2">
            <Button
              size="2"
              variant="soft"
              onClick={addAllFromBudgets}
              disabled={bulkAddingFromBudgets}
            >
              <IconUsers size={16} />
              {bulkAddingFromBudgets ? "Adding..." : "Track All from Budgets"}
            </Button>
            <Button size="2" onClick={() => setIsDialogOpen(true)}>
              <IconPlus size={16} />
              Add Creator
            </Button>
          </Flex>
        </Flex>

        <Text size="2" color="gray">
          Track all posts from these creators, regardless of sound, hashtags, or
          mentions.
        </Text>

        {!trackedCreators || trackedCreators.length === 0 ? (
          <Box
            p="4"
            style={{ border: "1px dashed var(--gray-7)", borderRadius: "8px" }}
          >
            <Text size="2" color="gray">
              No creators are currently being tracked. Add creators to
              automatically track all their posts.
            </Text>
          </Box>
        ) : (
          <Flex direction="column" gap="2">
            {trackedCreators?.map((tracked) => (
              <Flex
                key={tracked.id}
                justify="between"
                align="center"
                p="3"
                style={{
                  border: "1px solid var(--gray-6)",
                  borderRadius: "8px",
                  backgroundColor: "var(--gray-2)",
                }}
              >
                <Flex direction="column" gap="1">
                  <Flex align="center" gap="2">
                    <Text weight="medium">
                      {tracked.creatorPrice.creator.name}
                    </Text>
                    <Badge color="blue">{tracked.creatorPrice.platform}</Badge>
                    <Badge
                      color={getScrapeBadgeColor(
                        tracked.creatorPrice.lastScraped,
                        tracked.creatorPrice.lastAttempted
                      )}
                      size="1"
                    >
                      ●
                    </Badge>
                    {tracked.creatorPrice.creator.creatorType && (
                      <Badge color="gray">
                        {tracked.creatorPrice.creator.creatorType.tag}
                      </Badge>
                    )}
                  </Flex>
                  <Text size="2" color="gray">
                    {getSocialMediaUrl(
                      tracked.creatorPrice.platform,
                      tracked.creatorPrice.username
                    ) ? (
                      <Link
                        href={getSocialMediaUrl(
                          tracked.creatorPrice.platform,
                          tracked.creatorPrice.username
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "var(--accent-11)",
                          textDecoration: "none",
                        }}
                      >
                        @{tracked.creatorPrice.username}
                      </Link>
                    ) : (
                      `@${tracked.creatorPrice.username}`
                    )}
                    {tracked.creatorPrice.followers &&
                      ` • ${tracked.creatorPrice.followers.toLocaleString()} followers`}
                    {` • ${formatLastScraped(
                      tracked.creatorPrice.lastScraped,
                      tracked.creatorPrice.lastAttempted
                    )}`}
                  </Text>
                </Flex>
                <Flex gap="2">
                  <IconButton
                    size="2"
                    color="blue"
                    variant="ghost"
                    onClick={() => openScrapeDialog(tracked.creatorPrice.id)}
                    disabled={scrapingCreators.has(tracked.creatorPrice.id)}
                    title="Force scrape this creator"
                  >
                    <IconReload
                      size={16}
                      className={
                        scrapingCreators.has(tracked.creatorPrice.id)
                          ? "spinning"
                          : ""
                      }
                    />
                  </IconButton>
                  <IconButton
                    size="2"
                    color="red"
                    variant="ghost"
                    onClick={() =>
                      removeCreatorFromTracking(tracked.creatorPrice.id)
                    }
                    title="Remove from tracking"
                  >
                    <IconTrash size={16} />
                  </IconButton>
                </Flex>
              </Flex>
            ))}
          </Flex>
        )}

        <Dialog.Root open={isDialogOpen} onOpenChange={handleDialogClose}>
          <Dialog.Content style={{ maxWidth: 500, maxHeight: "80vh" }}>
            <Dialog.Title>Add Creator to Tracking</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Search for a creator to track all their posts in this campaign.
            </Dialog.Description>

            <Flex direction="column" gap="3">
              <TextField.Root
                placeholder="Search creators by name or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="3"
              >
                <TextField.Slot>
                  <IconSearch height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>

              {searchQuery.length > 0 && searchQuery.length < 2 && (
                <Text size="2" color="gray">
                  Type at least 2 characters to search...
                </Text>
              )}

              {searchQuery.length >= 2 && (
                <ScrollArea style={{ height: 300 }}>
                  <Flex direction="column" gap="2">
                    {availableCreatorPrices.length === 0 ? (
                      <Text
                        size="2"
                        color="gray"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {searchResults
                          ? "No creators found matching your search."
                          : "Searching..."}
                      </Text>
                    ) : (
                      availableCreatorPrices.map((creatorPrice) => (
                        <Card
                          key={creatorPrice.id}
                          style={{
                            padding: "12px",
                            cursor: "pointer",
                            border:
                              selectedCreator?.id === creatorPrice.id
                                ? "2px solid var(--accent-9)"
                                : "1px solid var(--gray-6)",
                          }}
                          onClick={() => handleCreatorSelect(creatorPrice)}
                        >
                          <Flex direction="column" gap="1">
                            <Flex align="center" gap="2">
                              <Text weight="medium">
                                {creatorPrice.creatorName}
                              </Text>
                              {creatorPrice.starred && (
                                <Badge color="yellow">⭐</Badge>
                              )}
                              <Badge color="blue">
                                {creatorPrice.platform}
                              </Badge>
                              {creatorPrice.creatorType && (
                                <Badge color="gray">
                                  {creatorPrice.creatorType.tag}
                                </Badge>
                              )}
                            </Flex>
                            <Text size="2" color="gray">
                              {getSocialMediaUrl(
                                creatorPrice.platform,
                                creatorPrice.username
                              ) ? (
                                <Link
                                  href={getSocialMediaUrl(
                                    creatorPrice.platform,
                                    creatorPrice.username
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "var(--accent-11)",
                                    textDecoration: "none",
                                  }}
                                >
                                  @{creatorPrice.username}
                                </Link>
                              ) : (
                                `@${creatorPrice.username}`
                              )}
                              {creatorPrice.followers &&
                                ` • ${creatorPrice.followers.toLocaleString()} followers`}
                            </Text>
                          </Flex>
                        </Card>
                      ))
                    )}
                  </Flex>
                </ScrollArea>
              )}

              {selectedCreator && (
                <>
                  <Separator />
                  <Box
                    p="3"
                    style={{
                      backgroundColor: "var(--accent-3)",
                      borderRadius: "8px",
                      border: "1px solid var(--accent-6)",
                    }}
                  >
                    <Text
                      size="2"
                      weight="medium"
                      style={{ color: "var(--accent-11)" }}
                    >
                      Selected:
                    </Text>
                    <Text size="2" style={{ color: "var(--accent-11)" }}>
                      {selectedCreator.creatorName} (
                      {getSocialMediaUrl(
                        selectedCreator.platform,
                        selectedCreator.username
                      ) ? (
                        <Link
                          href={getSocialMediaUrl(
                            selectedCreator.platform,
                            selectedCreator.username
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "var(--accent-11)",
                            textDecoration: "underline",
                          }}
                        >
                          @{selectedCreator.username}
                        </Link>
                      ) : (
                        `@${selectedCreator.username}`
                      )}{" "}
                      - {selectedCreator.platform})
                    </Text>
                  </Box>
                </>
              )}
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={addCreatorToTracking}
                disabled={!selectedCreator || loading}
              >
                {loading ? "Adding..." : "Add Creator"}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        <Dialog.Root
          open={isScrapeDialogOpen}
          onOpenChange={setIsScrapeDialogOpen}
        >
          <Dialog.Content style={{ maxWidth: 400 }}>
            <Dialog.Title>Force Scrape Creator</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Specify how many days of posts to scrape for this creator.
            </Dialog.Description>

            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Lookback Days (1-30)
                </Text>
                <TextField.Root
                  type="number"
                  min="1"
                  max="30"
                  value={lookbackDays}
                  onChange={(e) => setLookbackDays(e.target.value)}
                  placeholder="7"
                />
              </label>
              <Text size="1" color="gray">
                More days will scrape more posts but take longer to complete.
              </Text>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button onClick={forceScrapeCreator}>Start Scraping</Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Flex>
    </div>
  );
};

export default TrackedCreatorsSection;
